import { FC, useEffect, useMemo, useState } from 'react';
import './InvoiceInventory.css';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import eFormType from '../../../../main/assets/enums/eFormType';
import { ApiConstants } from '../../../../main/constants/ApiConstants';
import axiosInstance from '../../../../main/initializers/axios';
import IInvoice from '../../../../main/interfaces/IInvoice';
import IInvoiceRows from '../../../../main/interfaces/IInvoiceRows';
import GeneralInput from '../../../../main/components/generalInput/GeneralInput';
import useGetPermission from '../../../../main/hooks/useGetPermission';
import i18n from 'i18next';
import useGetUser from '../../../../main/hooks/useGetUser';
import { formatDateByTimeZone, getCompanyTimeZone } from '../../../../main/utils/timezone';

interface Props {
  tempInvoice?: any;
  modalType: eFormType;
  onSubmitData: (data: any) => void;
  createOrUpdate: boolean;
}

const isDeletedInvoiceRow = (row: IInvoiceRows) => !!((row as any).deleted ?? (row as any).Deleted);

const InvoiceInventoryComp: FC<Props> = (props: Props) => {
  const { tempInvoice, modalType, createOrUpdate } = props;

  const [invoiceRows, setInvoiceRows] = useState<IInvoiceRows[]>([]);
  const [isRefiscalizing, setIsRefiscalizing] = useState(false);
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);

  const user = useGetUser();
  const permissions = useGetPermission();
  const companyTimeZone = getCompanyTimeZone(user);
  const normalizePermissionValue = (value?: string) =>
    (value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '');

  const canEditAdminInvoice = permissions.some((permission: any) => {
    const permissionKeys = [permission?.url, permission?.code, permission?.name, permission?.title, permission?.label]
      .filter(Boolean)
      .map((value) => normalizePermissionValue(String(value)));

    return (
      permission?.isWrite === true &&
      permissionKeys.some(
        (key) =>
          key === 'updateadmininvoice' ||
          key.endsWith('/api/invoice/updateadmininvoice') ||
          key.endsWith('api/invoice/updateadmininvoice')
      )
    );
  });

  const canDeleteInvoiceRow = permissions.some((permission: any) => {
    const permissionKeys = [permission?.url, permission?.code, permission?.name, permission?.title, permission?.label]
      .filter(Boolean)
      .map((value) => normalizePermissionValue(String(value)));

    return (
      permission?.isWrite === true &&
      permissionKeys.some(
        (key) =>
          key === 'deleteinvoiceitem' ||
          key.endsWith('/api/invoice/row') ||
          key.endsWith('api/invoice/row')
      )
    );
  });

  const invoiceSequence = tempInvoice?.invoiceSequence ?? tempInvoice?.InvoiceSequence;
  const importedInvoiceNumber =
    invoiceSequence?.eBillsInvoiceNumber ?? invoiceSequence?.EBillsInvoiceNumber ?? tempInvoice?.eBillsInvoiceNumber ?? tempInvoice?.EBillsInvoiceNumber;
  const fiscalizedAt =
    invoiceSequence?.fiscalizedAt ?? invoiceSequence?.FiscalizedAt ?? tempInvoice?.fiscalizedAt ?? tempInvoice?.FiscalizedAt;
  const isFiscalized =
    invoiceSequence?.isFiscalized ?? invoiceSequence?.IsFiscalized ?? tempInvoice?.isFiscalized ?? tempInvoice?.IsFiscalized;
  const canRefiscalize =
    modalType === eFormType.Edit && !!tempInvoice?.id && !!importedInvoiceNumber && !!fiscalizedAt && isFiscalized !== true;

  const createdByUser = tempInvoice?.cratedByUser ?? tempInvoice?.CratedByUser ?? tempInvoice?.createdByUser ?? tempInvoice?.CreatedByUser;
  const createdByFullName = [createdByUser?.firstName ?? createdByUser?.FirstName, createdByUser?.lastName ?? createdByUser?.LastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const createdByName =
    createdByFullName ||
    createdByUser?.userName ||
    createdByUser?.UserName ||
    tempInvoice?.userName ||
    tempInvoice?.UserName ||
    '-';
  const createdAt = tempInvoice?.created ?? tempInvoice?.Created;
  const createdAtText = createdAt ? formatDateByTimeZone(createdAt, companyTimeZone) || String(createdAt) : '-';

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IInvoice>({
    defaultValues: {
      id: modalType === eFormType.Edit ? tempInvoice?.id : 0,
      name: modalType === eFormType.Edit ? tempInvoice?.name : '',
      price: modalType === eFormType.Edit ? tempInvoice?.price : 0,
      measurmentUnit: modalType === eFormType.Edit ? tempInvoice?.measurmentUnit : '',
    },
  });

  const invoiceTotal = useMemo(() => {
    return invoiceRows.reduce((sum, row) => {
      if (isDeletedInvoiceRow(row)) return sum;

      const price = Number(row.price) || 0;
      const quantity = Number(row.quantity) || 0;

      return sum + price * quantity;
    }, 0);
  }, [invoiceRows]);

  const buildUpdateParams = (data: any, rows: IInvoiceRows[]) => ({
    Id: data.id,
    Name: data.name,
    Price: data.price,
    MeasurmentUnit: data.measurmentUnit,
    InvoiceRows: rows
      .filter((row) => !isDeletedInvoiceRow(row))
      .map((row) => ({
        Id: row.id,
        Quantity: Number(row.quantity) || 1,
        Price: row.price,
      })),
  });

  const updateInvoice = async (data: any, rows: IInvoiceRows[], showSuccessToast = true) => {
    if (!canEditAdminInvoice) {
      toast.error(i18n.t('errors.noPermission', { defaultValue: 'You do not have permission for this action.' }));
      return false;
    }

    try {
      setIsSavingInvoice(true);
      const response = await axiosInstance.post(ApiConstants.invoice.updateAdminInvoice, buildUpdateParams(data, rows));

      if (response.status === 200) {
        const updatedInvoice = response.data;
        const updatedRows = updatedInvoice?.invoiceRows || updatedInvoice?.InvoiceRows || rows;

        setValue('price', updatedInvoice?.price ?? invoiceTotal);
        setInvoiceRows(updatedRows);

        if (showSuccessToast) {
          toast.success(i18n.t('orderDetails.createdSuccessfully'));
        }

        props.onSubmitData(true);
        return true;
      }

      toast.error(i18n.t('orderDetails.somethingWentWrong'));
      return false;
    } catch {
      toast.error(i18n.t('orderDetails.somethingWentWrong'));
      return false;
    } finally {
      setIsSavingInvoice(false);
    }
  };

  const onSubmit = (data: any) => {
    updateInvoice(data, invoiceRows);
  };

  const updateRowQuantity = (rowId: number | undefined, value: string) => {
    if (!canEditAdminInvoice) return;

    const quantity = Number(value);

    setInvoiceRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
            }
          : row
      )
    );
  };

  const deleteRow = async (rowId: number | undefined) => {
    if (!canDeleteInvoiceRow || !rowId || !tempInvoice?.id) {
      toast.error(i18n.t('errors.noPermission', { defaultValue: 'You do not have permission for this action.' }));
      return;
    }

    const row = invoiceRows.find((item) => item.id === rowId);
    const confirmed = window.confirm(
      i18n.t('orderDetails.confirmDeleteInvoiceItem', {
        defaultValue: `Are you sure you want to delete ${row?.product?.name ?? 'this item'} from the invoice?`,
        product: row?.product?.name ?? '',
      })
    );

    if (!confirmed) return;

    try {
      setIsSavingInvoice(true);
      const response = await axiosInstance.delete(ApiConstants.invoice.deleteRow, {
        params: { invoiceId: tempInvoice.id, rowId },
      });
      const updatedRows = response.data?.invoiceRows ?? response.data?.InvoiceRows;

      setInvoiceRows(Array.isArray(updatedRows) ? updatedRows : invoiceRows.filter((item) => item.id !== rowId));
      toast.success(i18n.t('orderDetails.invoiceItemDeleted', { defaultValue: 'Item deleted from invoice.' }));
      props.onSubmitData(true);
    } catch {
      toast.error(i18n.t('orderDetails.somethingWentWrong'));
    } finally {
      setIsSavingInvoice(false);
    }
  };

  const onRefiscalize = async () => {
    if (!tempInvoice?.id || !importedInvoiceNumber || isRefiscalizing) return;

    try {
      setIsRefiscalizing(true);
      await axiosInstance.post(`${ApiConstants.invoice.refiscalize}?invoiceId=${tempInvoice.id}`);
      toast.success(i18n.t('orderDetails.refiscalizedSuccessfully', { defaultValue: 'Invoice re-fiscalized successfully.' }));
      props.onSubmitData(true);
    } catch {
      toast.error(i18n.t('orderDetails.somethingWentWrong'));
    } finally {
      setIsRefiscalizing(false);
    }
  };

  useEffect(() => {
    if (!tempInvoice?.id) return;

    axiosInstance.get(ApiConstants.invoice.rows + '?invoiceId=' + tempInvoice.id).then((res) => {
      setInvoiceRows(res.data ?? []);
    });

    if (createOrUpdate && canEditAdminInvoice) {
      handleSubmit(onSubmit)();
    }
  }, [createOrUpdate, tempInvoice?.id, canEditAdminInvoice]);

  useEffect(() => {
    setValue('price', Number(invoiceTotal.toFixed(2)));
  }, [invoiceTotal, setValue]);

  return (
    <div className="invWrap">
      <div className="invGrid">
        <div className="bcCard invCard">
          <div className="invCardHeader">
            <div>
              <div className="invTitle">{i18n.t('orderDetails.InvoiceDetails')}</div>
              <div className="invSub">{i18n.t('orderDetails.EditInvoiceInfo')}</div>
            </div>
            {canRefiscalize && (
              <button type="button" className="invActionBtn" onClick={onRefiscalize} disabled={isRefiscalizing}>
                {isRefiscalizing
                  ? i18n.t('orderDetails.refiscalizing', { defaultValue: 'ReFiscalizing...' })
                  : i18n.t('orderDetails.refiscalize', { defaultValue: 'ReFiscalize' })}
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="invForm">
            <div className="invMeta">
              <div className="invMetaItem">
                <span className="invMetaLabel">{i18n.t('invoice.createdAt', { defaultValue: 'Created at' })}</span>
                <span className="invMetaValue">{createdAtText}</span>
              </div>
              <div className="invMetaItem">
                <span className="invMetaLabel">{i18n.t('invoice.createdBy', { defaultValue: 'Created by' })}</span>
                <span className="invMetaValue">{createdByName}</span>
              </div>
            </div>

            <div className="invRow">
              <div className="invField">
                <GeneralInput
                  required={false}
                  label={i18n.t('orderDetails.Name')}
                  name="name"
                  type="text"
                  placeholder={i18n.t('orderDetails.Name')}
                  errors={errors}
                  control={control}
                  disabled={!canEditAdminInvoice || isSavingInvoice}
                />
              </div>

              <div className="invField">
                <GeneralInput
                  required={false}
                  label={i18n.t('orderDetails.Price')}
                  name="price"
                  type="text"
                  placeholder={i18n.t('orderDetails.Price')}
                  errors={errors}
                  control={control}
                  disabled
                />
              </div>

              <div className="invField">
                <GeneralInput
                  required={false}
                  label={i18n.t('orderDetails.MeasurementUnit')}
                  name="measurmentUnit"
                  type="text"
                  placeholder={i18n.t('orderDetails.MeasurementUnit')}
                  errors={errors}
                  control={control}
                  disabled={!canEditAdminInvoice || isSavingInvoice}
                />
              </div>
            </div>
            <div className="invHint">
              {canEditAdminInvoice
                ? i18n.t('orderDetails.SaveChangesHint')
                : i18n.t('errors.noPermission', { defaultValue: 'You do not have permission for this action.' })}
            </div>

            {canEditAdminInvoice && (
              <div className="invFooter">
                <button
                  type="button"
                  className="invActionBtn invActionBtnPrimary"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSavingInvoice}
                >
                  {isSavingInvoice
                    ? i18n.t('buttons.saving', { defaultValue: 'Saving...' })
                    : i18n.t('buttons.approve', { defaultValue: 'Approve' })}
                </button>
              </div>
            )}
          </form>
        </div>

        <div className="bcCard invCard">
          <div className="invCardHeader">
            <div>
              <div className="invTitle">{i18n.t('orderDetails.InvoiceRows')}</div>
              <div className="invSub">
                {invoiceRows?.length ?? 0} {i18n.t('orderDetails.ItemsCount')}
              </div>
            </div>
          </div>

          <div className="invTableWrap">
            <table className="invTable">
              <thead>
                <tr>
                  <th style={{ width: 120 }}>{i18n.t('generic.price')}</th>
                  <th style={{ width: 120 }}>{i18n.t('generic.quantity')}</th>
                  <th>{i18n.t('generic.product')}</th>
                  {canDeleteInvoiceRow && <th className="invActionsCol"></th>}
                </tr>
              </thead>

              <tbody>
                {invoiceRows?.length ? (
                  invoiceRows.map((item) => {
                    const isDeletedRow = isDeletedInvoiceRow(item);

                    return (
                      <tr key={item.id} className={isDeletedRow ? 'invDeletedRow' : undefined}>
                        <td>{item.price}</td>

                        <td>
                          {canEditAdminInvoice && !isDeletedRow ? (
                            <input
                              className="invQtyInput"
                              type="number"
                              min="1"
                              value={item.quantity}
                              disabled={isSavingInvoice}
                              onChange={(e) => updateRowQuantity(item.id, e.target.value)}
                            />
                          ) : (
                            item.quantity
                          )}
                        </td>

                        <td>
                          <span>{item.product?.name ?? '-'}</span>
                          {isDeletedRow && (
                            <span className="invDeletedBadge">
                              {i18n.t('orderDetails.deletedInvoiceRow', { defaultValue: 'Deleted item' })}
                            </span>
                          )}
                        </td>

                        {canDeleteInvoiceRow && (
                          <td className="invActionsCell">
                            {!isDeletedRow && (
                              <button
                                type="button"
                                className="invDeleteBtn"
                                disabled={isSavingInvoice}
                                onClick={() => deleteRow(item.id)}
                              >
                                {i18n.t('buttons.delete', { defaultValue: 'Delete' })}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={canDeleteInvoiceRow ? 4 : 3} className="invEmpty">
                      {i18n.t('orderDetails.noRows', { defaultValue: 'No rows' })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="invTotal">
            {i18n.t('generic.total', { defaultValue: 'Total' })}: <strong>{invoiceTotal.toFixed(2)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceInventoryComp;
